import { motion } from "framer-motion";
import {
  Palette,
  Layers,
  Lightbulb,
  Loader2,
  Wand2,
  ArrowRight,
  Check,
} from "lucide-react";
import { ENDUITS, DECO, LIGHT, slugify } from "./constants";

function SectionHeader({ Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="w-8 h-8 rounded-lg bg-[#FF5A00]/15 text-[#FF5A00] flex items-center justify-center">
        <Icon size={16} />
      </div>
      <h4 className="text-sm uppercase tracking-[0.2em] text-white">{title}</h4>
    </div>
  );
}

function ToggleCard({ item, active, testId, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      className={`relative p-4 rounded-2xl border transition-all text-left min-h-[72px] ${
        active
          ? "border-[#FF5A00] bg-[#FF5A00]/8"
          : "border-white/10 hover:border-white/30 bg-white/[0.02]"
      }`}
    >
      <div className="text-sm text-white leading-snug pr-6">{item}</div>
      <div
        className={`absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
          active ? "border-[#FF5A00] bg-[#FF5A00]" : "border-white/30"
        }`}
      >
        {active && <Check size={12} className="text-white" />}
      </div>
    </button>
  );
}

/** Panneau d'options (couleur enduit, décor pierre, LED) + CTA régénérer. */
export function OptionsPanel({
  enduit,
  onEnduit,
  deco,
  onToggleDeco,
  light,
  onToggleLight,
  totalSelected,
  hasAnyOption,
  applyLoading,
  onRegenerate,
}) {
  return (
    <motion.div
      id="configurator-options"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      data-testid="configurator-options"
      className="glass-strong rounded-3xl p-6 md:p-8"
    >
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h3 className="font-display text-2xl md:text-3xl text-white mb-1">
            Personnalisez encore plus
          </h3>
          <p className="text-sm text-neutral-400">
            Ajoutez couleur, décor pierre ou éclairage LED sur votre proposition
          </p>
        </div>
        {totalSelected > 0 && (
          <div
            className="text-xs uppercase tracking-wider bg-[#FF5A00]/15 text-[#FF5A00] border border-[#FF5A00]/30 px-3 py-1.5 rounded-full"
            data-testid="configurator-selection-count"
          >
            {totalSelected} option{totalSelected > 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* Couleur enduit */}
      <div className="mb-8" data-testid="config-section-enduit">
        <SectionHeader Icon={Palette} title="Couleur enduit" />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {ENDUITS.map((e) => {
            const active = enduit?.key === e.key;
            return (
              <button
                key={e.key}
                type="button"
                onClick={() => onEnduit(active ? null : e)}
                data-testid={`config-enduit-${e.key}`}
                className={`relative p-3 rounded-2xl border transition-all text-left ${
                  active
                    ? "border-[#FF5A00] bg-[#FF5A00]/8"
                    : "border-white/10 hover:border-white/30 bg-white/[0.02]"
                }`}
              >
                <div
                  className="w-full aspect-square rounded-xl mb-2 border border-white/20"
                  style={{ background: e.color }}
                />
                <div className="text-xs text-white font-medium leading-tight">
                  {e.label}
                </div>
                {active && (
                  <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#FF5A00] flex items-center justify-center">
                    <Check size={12} className="text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Style décoratif (pierre) */}
      <div className="mb-8" data-testid="config-section-deco">
        <SectionHeader Icon={Layers} title="Décor pierre & soubassement" />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {DECO.map((item) => (
            <ToggleCard
              key={item}
              item={item}
              active={deco.includes(item)}
              testId={`config-deco-${slugify(item)}`}
              onToggle={() => onToggleDeco(item)}
            />
          ))}
        </div>
      </div>

      {/* Éclairage LED */}
      <div data-testid="config-section-light">
        <SectionHeader Icon={Lightbulb} title="Éclairage LED" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {LIGHT.map((item) => (
            <ToggleCard
              key={item}
              item={item}
              active={light.includes(item)}
              testId={`config-light-${slugify(item)}`}
              onToggle={() => onToggleLight(item)}
            />
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="mt-10 pt-8 border-t border-white/10 flex flex-col items-center gap-3">
        <button
          type="button"
          onClick={onRegenerate}
          disabled={applyLoading || !hasAnyOption}
          data-testid="configurator-regenerate-btn"
          className="btn-primary justify-center text-base px-8 py-4 w-full sm:w-auto disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {applyLoading ? (
            <>
              <Loader2 size={18} className="animate-spin" /> Génération…
            </>
          ) : (
            <>
              <Wand2 size={18} /> Régénérer ma façade
              <ArrowRight size={16} />
            </>
          )}
        </button>
        {!hasAnyOption && (
          <p className="text-xs text-neutral-500 text-center">
            Sélectionnez au moins une option pour régénérer
          </p>
        )}
      </div>
    </motion.div>
  );
}
